import { LeadDetail } from '#interfaces/lead/lead.interface';
import { extractPhone } from '#utils/lead/share-target';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { QuickLeadDialogComponent } from 'app/modules/admin/crm/leads/dialogs/quick-lead-dialog.component';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TextFieldModule } from '@angular/cdk/text-field';
import { DOCUMENT, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostBinding,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { UserStorageService } from '#root/core/user/user.service';
import { QuickChatService } from 'app/layout/common/quick-chat/quick-chat.service';
import { ChatMessage, ChatSession } from 'app/layout/common/quick-chat/quick-chat.types';
import { EMPTY, Subject, filter, finalize, map, switchMap, take, takeUntil, timeout } from 'rxjs';

@Component({
    selector: 'quick-chat',
    templateUrl: './quick-chat.component.html',
    styleUrls: ['./quick-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'quickChat',
    imports: [
        NgClass,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        FuseScrollbarDirective,
        NgTemplateOutlet,
        MatFormFieldModule,
        MatInputModule,
        TextFieldModule,
        DatePipe,
    ]
})
export class QuickChatComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('messageInput') messageInput: ElementRef;
    sessions: ChatSession[] = [];
    selectedSession: ChatSession | null = null;
    messages: ChatMessage[] = [];
    visitorTyping: boolean = false;
    opened: boolean = false;

    private _mutationObserver: MutationObserver;
    private _scrollStrategy: ScrollStrategy;
    private _overlay: HTMLElement;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _typingTimeout: any;

    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _ngZone: NgZone,
        private _quickChatService: QuickChatService,
        private _userService: UserStorageService,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private _dialog: MatDialog,
        private _router: Router
    ) {
        this._scrollStrategy = this._scrollStrategyOptions.block();
    }

    @HostBinding('class') get classList(): any {
        return {
            'quick-chat-opened': this.opened,
        };
    }

    ngOnInit(): void {
        this._quickChatService.loadSessions();

        // Notificación de chat: abre el panel en esa conversación en cuanto aparece en la lista.
        this._quickChatService.openRequest$
            .pipe(
                filter((sessionId): sessionId is number => !!sessionId),
                switchMap((sessionId) => {
                    this.open();
                    this._quickChatService.loadSessions();
                    return this._quickChatService.sessions$.pipe(
                        map((sessions) => (sessions || []).find((s) => s.id === sessionId)),
                        filter((session): session is ChatSession => !!session),
                        take(1),
                        // Si ya se cerró, queda abierta la lista de conversaciones.
                        timeout({ first: 5000, with: () => EMPTY }),
                        finalize(() => this._quickChatService.clearOpenRequest())
                    );
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((session) => this.selectSession(session));

        this._quickChatService.sessions$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((sessions: ChatSession[]) => {
                (sessions || []).forEach(s => this._ensureUtcDates(s, 'createdAt', 'lastMessageAt', 'closedAt'));
                this.sessions = sessions || [];
            });

        this._quickChatService.selectedSession$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((session: ChatSession | null) => {
                this.selectedSession = session;
            });

        this._quickChatService.messages$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((messages: ChatMessage[]) => {
                (messages || []).forEach(m => this._ensureUtcDates(m, 'sentAt'));
                this.messages = messages || [];
            });

        this._quickChatService.newMessage$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((message: ChatMessage | null) => {
                if (message) {
                    this._ensureUtcDates(message, 'sentAt');
                }
                if (
                    message &&
                    this.selectedSession &&
                    message.chatSessionId === this.selectedSession.id
                ) {
                    this._quickChatService.addMessage(message);
                }
            });

        this._quickChatService.typingIndicator$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((data: any) => {
                if (data && data.senderType === 'visitor') {
                    this.visitorTyping = data.isTyping;
                }
            });
    }

    ngAfterViewInit(): void {
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const mutationTarget = mutation.target as HTMLElement;
                if (mutation.attributeName === 'class') {
                    if (
                        mutationTarget.classList.contains(
                            'cdk-global-scrollblock'
                        )
                    ) {
                        const top = parseInt(mutationTarget.style.top, 10);
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            `${Math.abs(top)}px`
                        );
                    } else {
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            null
                        );
                    }
                }
            });
        });
        this._mutationObserver.observe(this._document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    /**
     * Convierte la conversación del chat web en un lead: abre el alta rápida con el nombre del visitante
     * y los últimos mensajes como nota (el teléfono se pide al cliente en el chat).
     */
    createLeadFromChat(): void {
        if (!this.selectedSession) return;
        const visitorLines = this.messages
            .filter(m => m.senderType === 'visitor')
            .slice(-5)
            .map(m => m.content.trim())
            .join('\n');
        const email = this.selectedSession.visitorEmail ? `Correo: ${this.selectedSession.visitorEmail}\n` : '';
        this._dialog.open(QuickLeadDialogComponent, {
            width: '480px',
            maxWidth: '95vw',
            data: {
                contactName: this.selectedSession.visitorName,
                phone: extractPhone(visitorLines),
                note: `Chat web\n${email}${visitorLines}`.slice(0, 600),
            },
        }).afterClosed().subscribe((lead: LeadDetail | undefined) => {
            if (lead) this._router.navigate(['/crm/leads', lead.id]);
        });
    }

    ngOnDestroy(): void {
        this._mutationObserver.disconnect();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    open(): void {
        if (this.opened) return;
        this._toggleOpened(true);
    }

    close(): void {
        if (!this.opened) return;
        this._quickChatService.clearSelection();
        this._toggleOpened(false);
    }

    toggle(): void {
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    selectSession(session: ChatSession): void {
        this._toggleOpened(true);
        this._quickChatService.selectSession(session);
    }

    backToList(): void {
        this._quickChatService.clearSelection();
    }

    sendMessage(): void {
        if (!this.messageInput || !this.selectedSession) return;
        const content = this.messageInput.nativeElement.value?.trim();
        if (!content) return;

        const user = this._userService.get();
        this._quickChatService
            .sendMessage(
                this.selectedSession.id,
                content,
                atob(user.id),
                user.name,
                this.selectedSession.sessionIdentifier
            )
            .subscribe(() => {
                this.messageInput.nativeElement.value = '';
            });
    }

    assignToMe(): void {
        if (!this.selectedSession) return;
        const user = this._userService.get();
        this._quickChatService
            .assignAdmin(this.selectedSession.id, atob(user.id))
            .subscribe();
    }

    closeChat(): void {
        if (!this.selectedSession) return;
        this._quickChatService
            .closeSession(this.selectedSession.id)
            .subscribe(() => {
                this._quickChatService.clearSelection();
            });
    }

    onInputKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    onInputChange(): void {
        if (this.selectedSession) {
            this._quickChatService.sendTypingIndicator(
                this.selectedSession.sessionIdentifier,
                true
            );
            clearTimeout(this._typingTimeout);
            this._typingTimeout = setTimeout(() => {
                if (this.selectedSession) {
                    this._quickChatService.sendTypingIndicator(
                        this.selectedSession.sessionIdentifier,
                        false
                    );
                }
            }, 2000);
        }
    }

    getStatusLabel(status: number): string {
        switch (status) {
            case 0:
                return 'Esperando';
            case 1:
                return 'Activo';
            case 2:
                return 'Cerrado';
            default:
                return '';
        }
    }

    getStatusClass(status: number): string {
        switch (status) {
            case 0:
                return 'bg-yellow-500';
            case 1:
                return 'bg-green-500';
            case 2:
                return 'bg-gray-400';
            default:
                return '';
        }
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    linkify(content: string): string {
        if (!content) return '';
        const escaped = content
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        return escaped.replace(
            /(https?:\/\/[^\s<]+)/g,
            '<a href="$1" target="_blank" rel="noopener noreferrer" class="underline break-all">$1</a>'
        );
    }

    /** Ensure datetime strings from the API are treated as UTC */
    private _ensureUtcDates(obj: any, ...fields: string[]): void {
        for (const field of fields) {
            if (obj[field] && typeof obj[field] === 'string' && !obj[field].endsWith('Z')) {
                obj[field] = obj[field] + 'Z';
            }
        }
    }

    private _showOverlay(): void {
        this._hideOverlay();
        this._overlay = this._renderer2.createElement('div');
        if (!this._overlay) return;
        this._overlay.classList.add('quick-chat-overlay');
        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._overlay
        );
        this._scrollStrategy.enable();
        this._overlay.addEventListener('click', () => {
            this.close();
        });
    }

    private _hideOverlay(): void {
        if (!this._overlay) return;
        if (this._overlay) {
            this._overlay.parentNode.removeChild(this._overlay);
            this._overlay = null;
        }
        this._scrollStrategy.disable();
    }

    private _toggleOpened(open: boolean): void {
        this.opened = open;
        if (open) {
            this._showOverlay();
        } else {
            this._hideOverlay();
        }
    }
}
